import { css, cx } from "@linaria/core";
import type { ReactNode } from "react";
import { Component } from "react";

import { pm2Header } from "@pm2/design-tokens";
import {
  HttpStatus,
  textColumnCls,
  verticalPaddingCls,
} from "@pm2/react-utils";
import { messageBaseCss } from "@pm2/ui-components/selectors";
import { Link } from "@provider/navigation";
import { configRefs } from "@utils/constants";

type State = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<
  { children: ReactNode | ReactNode[]; currentPath: string },
  State
> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  public componentDidUpdate(
    prevProps: Readonly<{
      children: ReactNode | ReactNode[];
      currentPath: string;
    }>,
  ): void {
    if (prevProps.currentPath !== this.props.currentPath) {
      this.setState({ hasError: false });
    }
  }

  public componentDidCatch(error: unknown, errorInfo: unknown): void {
    console.error(error, errorInfo);
  }

  public render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className={wrapperCls}>
          <article
            className={cx("gPortal-page", textColumnCls, verticalPaddingCls)}
          >
            <HttpStatus code={500} />

            <h1 className={messageBaseCss.headingCls}>Noget gik galt</h1>

            <p className={messageBaseCss.textCls}>
              Der opstod desværre en fejl, da vi forsøgte at hente siden.
            </p>
            <p className={messageBaseCss.textCls}>
              Vi anbefaler du forsøger at genindlæse siden.
              <br />
              Hvis fejlen fortsat opstår, kan du oprette en supportsag, så vil
              vi hurtigst muligt løse problemet.
            </p>

            <div className={messageBaseCss.buttonWrapperCls}>
              <Link
                href={window.location.href}
                onClick={this.refreshPage}
                className={messageBaseCss.buttonCls}
              >
                Genindlæs siden
              </Link>
              <a
                href={`${configRefs.urls.gu.dangerouslyAssertSyncValue()}/kundeservice/teknisk-support`}
                className={messageBaseCss.buttonCls}
                target="_blank"
                rel="noreferrer noopener"
              >
                Opret supportsag
              </a>
            </div>
          </article>
        </div>
      );
    }

    return this.props.children;
  }

  private refreshPage = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    history.go(0);
  };
}

const wrapperCls = css`
  /**
   * add spacer to fill out if breadcrumbs are available on the current page.
   */
  padding-top: ${pm2Header.breadcrumbs.computed()};

  @media print {
    padding-top: 0;
  }
`;
